import StyledTableHead from 'Components/StyledTableHead'
import type { AffiliateTicketOrderLineShape } from 'Services/SalesManagement/Features/Order/orderSlice'
import type {
  AffiliateOrderRefundOptions,
  AffiliateOrderRefundShape
} from 'Services/SalesManagement/Features/Order/types'
import { datetimeFormatter } from 'Util/formatters'
import { LinearProgress, Paper } from '@mui/material'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import type { FC } from 'react'
import { t } from 'ttag'

const RefundHistoryList: FC<{
  refundList: AffiliateOrderRefundShape[]
  ticketOrderLines: AffiliateTicketOrderLineShape[]
  affiliateOrderRefundOptions: { field_choices: AffiliateOrderRefundOptions } | undefined
  isRefundOptionsLoading: boolean
  isRefundListLoading: boolean
}> = ({
  refundList,
  ticketOrderLines,
  affiliateOrderRefundOptions,
  isRefundOptionsLoading,
  isRefundListLoading
}) => {
  const getRefundReasonByOptionId = (optionId: number) => {
    const foundOptions = affiliateOrderRefundOptions?.field_choices?.reason.find(
      option => option[0] === optionId
    )
    if (foundOptions) return foundOptions[1]
    return '-'
  }

  /**
   * Maps a refund row to its refunded amount.
   *
   * The refunded amounts live on the order lines' refund_summary, linked to a refund
   * row via external_refund_id === refund.id. Whole order refunds can span several
   * lines, so the amount is summed across lines. Returns null if no line matches.
   */
  const getRefundedAmount = (externalRefundId: string) => {
    let refundedAmount = 0
    let matched = false

    ticketOrderLines.forEach(line => {
      line.refund_summary.refunds?.forEach(refund => {
        if (String(refund.id) === externalRefundId) {
          matched = true
          refundedAmount += refund.amount
        }
      })
    })

    if (!matched) return null
    return refundedAmount
  }

  /**
   * Orphaned refunds are refunds that are created via Pusatec so there is no corresponding
   * refund row in manager's backend but only in walttistore.
   *
   * The orphaned refunds are identified by checking if the external_refund_id of the refund row
   * exists in the refundList. If it does not exist, it is considered an orphaned refund.
   */
  const matchedRefundIds = new Set(refundList.map(row => row.external_refund_id))

  const orphanRefunds = ticketOrderLines.flatMap(line =>
    line.refund_summary.refunds
      ? line.refund_summary.refunds
          .filter(refund => !matchedRefundIds.has(String(refund.id)))
          .map(refund => ({
            id: refund.id,
            created: refund.created,
            refundedAmount: refund.amount
          }))
      : []
  )

  const hasRows = refundList.length > 0 || orphanRefunds.length > 0

  const totalRemainingRefundable = ticketOrderLines.reduce(
    (sum, line) => sum + (line.refund_summary.refundable_amount ?? 0),
    0
  )

  const isLoading = isRefundOptionsLoading || isRefundListLoading

  return (
    <TableContainer component={Paper} sx={{ mb: 3 }}>
      <Table>
        <StyledTableHead>
          <TableRow>
            <TableCell colSpan={5}>{t`Refunds`}</TableCell>
          </TableRow>
        </StyledTableHead>
        {isLoading ? (
          <LinearProgress />
        ) : (
          <>
            <TableHead>
              <TableRow>
                <TableCell>{t`Created`}</TableCell>
                <TableCell>{t`Created by`}</TableCell>
                <TableCell>{t`Reason`}</TableCell>
                <TableCell>{t`Refund target`}</TableCell>
                <TableCell>{t`Refunded amount`}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hasRows ? (
                <>
                  {refundList.map(row => {
                    const refundedAmount = getRefundedAmount(row.external_refund_id)
                    return (
                      <TableRow key={`refund-${row.external_refund_id}-${row.ticket_id}`}>
                        <TableCell>{datetimeFormatter(row.created)}</TableCell>
                        <TableCell>{row.user.email}</TableCell>
                        <TableCell>{getRefundReasonByOptionId(row.reason)}</TableCell>
                        <TableCell>{row.ticket_id ? t`Single ticket` : t`Whole order`}</TableCell>
                        <TableCell>
                          {refundedAmount === null ? '-' : `${refundedAmount.toFixed(2)} €`}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                  {orphanRefunds.map(orphan => (
                    <TableRow key={`orphan-${orphan.id}`}>
                      <TableCell>{datetimeFormatter(orphan.created)}</TableCell>
                      <TableCell>Bonwal</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>{t`Single ticket`}</TableCell>
                      <TableCell>{orphan.refundedAmount.toFixed(2)} €</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>
                      {t`Remaining refundable amount`}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {totalRemainingRefundable.toFixed(2)} €
                    </TableCell>
                  </TableRow>
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={5}>{t`No refunds`}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </>
        )}
      </Table>
    </TableContainer>
  )
}

export { RefundHistoryList }
